import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const DEFAULT_BREAKPOINTS = {
  mobile: 1180,
  full: 1320,
  compact: 768,
  topbarCompact: 960,
};

const resolveCssLength = (value, fallback) => {
  const numericValue = Number.parseFloat(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  if (value.trim().endsWith('rem')) {
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);

    return numericValue * (Number.isFinite(rootFontSize) ? rootFontSize : 16);
  }

  return numericValue;
};

const resolveContentWidth = (element, fallback) => {
  const measuredWidth = element.getBoundingClientRect().width;

  if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) {
    return fallback;
  }

  const styles = getComputedStyle(element);
  const paddingInline = Number.parseFloat(styles.paddingLeft || '0')
    + Number.parseFloat(styles.paddingRight || '0');
  const contentWidth = measuredWidth - (Number.isFinite(paddingInline) ? paddingInline : 0);

  return contentWidth > 0 ? Math.round(contentWidth) : fallback;
};

export const useShopManagementLayout = ({ shell, topbarHost, sidebarOpen }) => {
  const shellWidth = ref(DEFAULT_BREAKPOINTS.mobile);
  const topbarWidth = ref(DEFAULT_BREAKPOINTS.full);
  const topbarHeight = ref(0);
  const breakpoints = ref({ ...DEFAULT_BREAKPOINTS });
  let resizeObserver = null;

  const isMobileShell = computed(() => shellWidth.value < breakpoints.value.mobile);
  const isNarrowTopbar = computed(
    () => topbarWidth.value < breakpoints.value.compact,
  );
  const topbarMode = computed(() => {
    if (topbarWidth.value >= breakpoints.value.full) {
      return 'full';
    }

    if (topbarWidth.value >= breakpoints.value.topbarCompact) {
      return 'compact';
    }

    return 'icon-first';
  });

  const syncLayoutBreakpoints = () => {
    if (!shell.value) {
      return;
    }

    const styles = getComputedStyle(shell.value);
    breakpoints.value = {
      mobile: resolveCssLength(
        styles.getPropertyValue('--p-bayhive-layout-breakpoint-mobile'),
        DEFAULT_BREAKPOINTS.mobile,
      ),
      full: resolveCssLength(
        styles.getPropertyValue('--p-bayhive-layout-breakpoint-full'),
        DEFAULT_BREAKPOINTS.full,
      ),
      compact: resolveCssLength(
        styles.getPropertyValue('--p-bayhive-layout-breakpoint-compact'),
        DEFAULT_BREAKPOINTS.compact,
      ),
      topbarCompact: resolveCssLength(
        styles.getPropertyValue('--p-bayhive-layout-topbar-compact-min-width'),
        DEFAULT_BREAKPOINTS.topbarCompact,
      ),
    };
  };

  const syncContainerMetrics = () => {
    if (!shell.value || !topbarHost.value) {
      return;
    }

    const wasMobile = isMobileShell.value;
    shellWidth.value = Math.round(shell.value.getBoundingClientRect().width || shellWidth.value);
    topbarWidth.value = resolveContentWidth(topbarHost.value, topbarWidth.value);
    topbarHeight.value = Math.ceil(topbarHost.value.getBoundingClientRect().height);

    if (wasMobile !== isMobileShell.value) {
      sidebarOpen.value = false;
    }
  };

  const stopObserving = () => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  };

  const startObserving = () => {
    if (!shell.value || !topbarHost.value) {
      return;
    }

    syncLayoutBreakpoints();
    syncContainerMetrics();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    stopObserving();
    resizeObserver = new ResizeObserver(syncContainerMetrics);
    resizeObserver.observe(shell.value);
    resizeObserver.observe(topbarHost.value);
  };

  onMounted(() => nextTick(startObserving));
  onBeforeUnmount(stopObserving);

  return {
    breakpoints,
    isMobileShell,
    shellWidth,
    isNarrowTopbar,
    topbarMode,
    topbarHeight,
  };
};
